import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@workspace/ui/lib/utils";

const buttonVariants = cva(
  // Updated transitions: avoid transition-all (recommended by CodeRabbit)
  "hover:cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg transition-[color,background-color,border-color,box-shadow,transform] duration-300 disabled:pointer-events-none disabled:opacity-50 aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:focus-visible:ring-offset-background aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-linear-to-br from-[#f8c43a] to-[#E1A224] text-[#0f0f0f] font-semibold hover:from-[#E1A224] hover:to-[#C08A1D] hover:shadow-lg hover:shadow-primary/25 dark:hover:shadow-primary/30 active:scale-[0.98]",

        outline:
          "border-2 border-border bg-transparent text-foreground hover:bg-accent/10 hover:border-accent dark:border-border dark:hover:bg-accent/5 dark:hover:border-accent font-medium",

        ghost: "bg-transparent text-foreground hover:bg-accent/10 dark:hover:bg-accent/5 font-medium active:scale-[0.98]",

        secondary:
          "bg-secondary text-secondary-foreground font-semibold hover:bg-secondary-variant dark:bg-surface-elevated dark:text-foreground dark:hover:bg-surface-hover active:scale-[0.98]",

        destructive:
          "bg-destructive text-white font-semibold hover:bg-[#B71C1C] dark:bg-destructive/80 dark:hover:bg-destructive hover:shadow-lg hover:shadow-destructive/25 focus-visible:ring-destructive/40 active:scale-[0.98]",

        link: "text-primary-text dark:text-primary underline-offset-4 hover:underline font-medium",

        success:
          "bg-success text-white font-semibold hover:bg-[#1B5E20] dark:bg-success/80 dark:hover:bg-success hover:shadow-lg hover:shadow-success/25 active:scale-[0.98]",
      },

      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3 text-base",
        xs: "h-7 rounded-md gap-1 px-2 has-[>svg]:px-1.5 text-xs",
        sm: "h-8 rounded-lg gap-1.5 px-3 has-[>svg]:px-2.5 text-sm",
        lg: "h-11 rounded-lg px-6 has-[>svg]:px-5 text-base",
        xl: "h-12 rounded-xl px-8 has-[>svg]:px-6 text-lg",
        icon: "size-9 rounded-lg",
        "icon-sm": "size-7 rounded",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return <Comp data-slot="button" data-variant={variant} data-size={size} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

Button.displayName = "Button";

export { Button, buttonVariants };
